import { defineMessages, IntlShape } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import { AreaFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import localSettings from "@app/config/LocalSettings";
import { METERS_IN_MILE } from "@app/util/consts";
import { isEntityValue } from "@app/util/filters/Location/assert";
import { SourceObjectMessageArgs, STANDARD_SOURCE_OBJECT_INTL_KEY } from "@app/util/ui";

// Without a loaded record the preview names the entity type, as before
const STANDARD_ARGS: SourceObjectMessageArgs = {
  label: undefined,
  sourceObject: STANDARD_SOURCE_OBJECT_INTL_KEY,
};

const messages = defineMessages({
  entityImperial: {
    id: "filters.location.previewMessage.entity.imperial",
    defaultMessage:
      "{miles} {miles, plural, one {mile} other {miles}} from {entityName, select, false {{sourceObject, select, standard {{entityType, select, accounts {the Company} contacts {the Person} other {the Record}}} leadCompanies {the Lead} siteCompanies {the Site} projectCompanies {the Project} shopCompanies {the Shop} partnerPeople {the Partner} leadPeople {the Lead} staffMemberPeople {the Staff Member} leadPersonnel {the Lead Personnel} other {the {label}}}} other {{entityName}}}",
    description:
      'Human readable location filter preview when using imperial units and filtering around some record. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  entitySi: {
    id: "filters.location.previewMessage.entity.si",
    defaultMessage:
      "{km}km from {entityName, select, false {{sourceObject, select, standard {{entityType, select, accounts {the Company} contacts {the Person} other {the Record}}} leadCompanies {the Lead} siteCompanies {the Site} projectCompanies {the Project} shopCompanies {the Shop} partnerPeople {the Partner} leadPeople {the Lead} staffMemberPeople {the Staff Member} leadPersonnel {the Lead Personnel} other {the {label}}}} other {{entityName}}}",
    description:
      'Human readable location filter preview when using SI units and filtering around some record. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  imperial: {
    id: "filters.location.previewMessage.imperial",
    defaultMessage:
      "{miles} {miles, plural, one {mile} other {miles}} from {hasLocation, select, true {{location}} other {({latitude}, {longitude})}}",
    description: "Human readable location filter preview when using imperial units",
  },
  si: {
    id: "filters.location.previewMessage.si",
    defaultMessage:
      "{km}km from {hasLocation, select, true {{location}} other {({latitude}, {longitude})}}",
    description: "Human readable location filter preview when using SI units",
  },
});

const getLocationPreview = (
  intl: IntlShape | undefined,
  value: AreaFilterCondition["value"],
  entity?: Company | Person,
  sourceObjectArgs: SourceObjectMessageArgs = STANDARD_ARGS
): string => {
  const useSiUnits = localSettings.doesUseSiUnits();
  if (!intl) {
    if (isEntityValue(value)) {
      const name =
        (entity?.name ?? value.entity.type === EntityType.COMPANY)
          ? "the Company"
          : value.entity.type === EntityType.PERSON
            ? "the Person"
            : "the Record";
      return useSiUnits
        ? `${value.distanceInMeters / 1000}km from ${name}`
        : `${value.distanceInMeters / METERS_IN_MILE} miles from ${name}`;
    } else {
      return useSiUnits
        ? `${value.distanceInMeters / 1000}km from ${
            value.location ?? `(${value.latitude}, ${value.longitude})`
          }`
        : `${value.distanceInMeters / METERS_IN_MILE} miles from ${
            value.location ?? `(${value.latitude}, ${value.longitude})`
          }`;
    }
  }

  if (isEntityValue(value)) {
    return intl.formatMessage(useSiUnits ? messages.entitySi : messages.entityImperial, {
      ...sourceObjectArgs,
      // an empty name reads as no name, so the preview names the record's type instead
      entityName: entity?.name || false,
      entityType: value.entity.type,
      km: value.distanceInMeters / 1000,
      miles: value.distanceInMeters / METERS_IN_MILE,
    });
  } else {
    let latitude;
    let longitude;

    if (!value.location) {
      // better format for numbers if we're gonna display then (and we're gonna since condition has no location string)
      latitude = intl.formatNumber(value.latitude, {
        maximumFractionDigits: 6,
        minimumFractionDigits: 1,
      });
      longitude = intl.formatNumber(value.longitude, {
        maximumFractionDigits: 6,
        minimumFractionDigits: 1,
      });
    }

    return intl.formatMessage(useSiUnits ? messages.si : messages.imperial, {
      hasLocation: !!value.location,
      km: value.distanceInMeters / 1000,
      latitude,
      location: value.location,
      longitude,
      miles: value.distanceInMeters / METERS_IN_MILE,
    });
  }
};

export default getLocationPreview;
